import { useEffect, useRef, useState } from 'react'
import { useAppStore } from '../../store/app'
import {
  deleteHousehold,
  householdDeletionPreview,
  requestAuthCode,
  requestStepUpToken,
  type HouseholdDeletionPreview,
  type HouseholdDeletionResult,
} from '../../api/client'
import { Group, SectionHeader } from '../account/AccountComponents'
import '../account/AccountStyles.css'
import './DeleteHouseholdSheet.css'

// Deleting a household, one step per screen: what goes, your computers,
// things outside Hearth, confirm (typed name + emailed code), done. Steps with
// nothing to say are skipped. Permanent: there is no undo, which is why the
// wizard spells out every consequence before it asks. See
// docs/household-deletion-plan.md.

type Step = 'what' | 'computers' | 'elsewhere' | 'confirm' | 'deleting' | 'done'

export function DeleteHouseholdSheet({ organizationID }: { organizationID: string }) {
  const email = useAppStore((s) => s.email) ?? ''
  const popSheet = useAppStore((s) => s.popSheet)

  const [preview, setPreview] = useState<HouseholdDeletionPreview | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [step, setStep] = useState<Step>('what')
  const [acknowledged, setAcknowledged] = useState<Record<string, boolean>>({})
  const [typedName, setTypedName] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<HouseholdDeletionResult | null>(null)
  const codeRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    householdDeletionPreview(organizationID)
      .then(setPreview)
      .catch((e) => setLoadError(e instanceof Error ? e.message : String(e)))
  }, [organizationID])

  useEffect(() => {
    if (codeSent) setTimeout(() => codeRef.current?.focus(), 50)
  }, [codeSent])

  if (loadError) {
    return (
      <div className="about">
        <p className="about__error">{loadError}</p>
      </div>
    )
  }
  if (!preview) {
    return (
      <div className="about">
        <p className="about__hint">Loading…</p>
      </div>
    )
  }

  const p = preview
  const hosts = p.hosts ?? []
  const devices = p.devices ?? []
  const connections = p.connections ?? []
  const others = (p.members ?? []).filter((m) => !m.is_you)
  const manualHosts = hosts.filter((h) => !h.auto_cleanup)
  const steps: Step[] = [
    'what',
    ...(hosts.length > 0 ? (['computers'] as Step[]) : []),
    ...(devices.length > 0 || connections.length > 0 ? (['elsewhere'] as Step[]) : []),
    'confirm',
  ]
  const nameMatches =
    typedName.trim() !== '' && typedName.trim().toLowerCase() === p.name.trim().toLowerCase()

  function next() {
    const i = steps.indexOf(step)
    setError(null)
    if (i >= 0 && i < steps.length - 1) setStep(steps[i + 1])
  }
  function back() {
    const i = steps.indexOf(step)
    setError(null)
    if (i > 0) setStep(steps[i - 1])
  }

  async function sendCode() {
    if (!nameMatches || busy) return
    setBusy(true)
    setError(null)
    try {
      await requestAuthCode(email)
      setCodeSent(true)
    } catch {
      setError('Could not send the code. Check your connection and try again.')
    } finally {
      setBusy(false)
    }
  }

  async function confirmDelete() {
    if (code.length !== 6 || busy || !nameMatches) return
    setBusy(true)
    setError(null)
    let stepupToken: string
    try {
      stepupToken = await requestStepUpToken(email, code)
    } catch {
      setBusy(false)
      setCode('')
      setError('That code didn’t work. Check it, or send a new one.')
      return
    }
    setStep('deleting')
    try {
      const res = await deleteHousehold({ organizationID, confirmName: typedName.trim(), stepupToken })
      setResult(res)
      setStep('done')
    } catch (e) {
      // The email code is spent either way; start the confirmation over.
      setStep('confirm')
      setCodeSent(false)
      setCode('')
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  function onCodeChange(value: string) {
    const digits = value.replace(/\D/g, '').slice(0, 6)
    setCode(digits)
  }

  const stepNumber = steps.indexOf(step) + 1
  const progress =
    stepNumber > 0 ? <p className="delete-household__progress">Step {stepNumber} of {steps.length}</p> : null

  // ---- 1. What will be deleted ----
  if (step === 'what') {
    const c = p.counts
    const lines = [
      plural(c.agents, 'agent'),
      plural(c.routines, 'routine'),
      c.chat_rooms > 0
        ? `${plural(c.chat_rooms, 'chat room')} with ${plural(c.messages, 'message')} and ${plural(c.files, 'file')}`
        : '',
      plural(c.connections, 'connected account'),
    ].filter(Boolean)
    return (
      <div className="about">
        {progress}
        <SectionHeader>Everything in {p.name} will be deleted</SectionHeader>
        <Group>
          <div className="delete-household__body">
            {lines.length > 0 ? (
              <ul className="delete-household__list">
                {lines.map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            ) : (
              <p>This household is empty.</p>
            )}
            <p>This is permanent. There’s no undo, and nothing can be recovered afterwards.</p>
          </div>
        </Group>

        {others.length > 0 && (
          <>
            <SectionHeader>These people lose access</SectionHeader>
            <Group>
              <div className="delete-household__body">
                {others.map((m) => (
                  <div key={m.human_user_id} className="delete-household__row">
                    <span className="delete-household__row-name">{m.name || m.email}</span>
                    <span className="delete-household__row-meta">{m.role === 'owner' ? 'Owner' : 'Member'}</span>
                  </div>
                ))}
                <p className="delete-household__note">We’ll email them to let them know.</p>
              </div>
            </Group>
          </>
        )}

        {p.last_household && (
          <p className="about__hint delete-household__hint">
            This is your only household. Afterwards you can create a new one or join one you’re invited to.
          </p>
        )}

        <StepButtons onNext={next} />
      </div>
    )
  }

  // ---- 2. Your computers ----
  if (step === 'computers') {
    const allAcknowledged = manualHosts.every((h) => acknowledged[h.host_id])
    return (
      <div className="about">
        {progress}
        <SectionHeader>Computers running Hearth for {p.name}</SectionHeader>
        <Group>
          <div className="delete-household__body">
            {hosts.map((h) => (
              <div key={h.host_id} className="delete-household__host">
                <div className="delete-household__row">
                  <span className="delete-household__row-name">{h.hostname}</span>
                  <span className="delete-household__row-meta">
                    {h.online ? 'Online' : 'Offline'}
                    {h.owner ? ` · ${h.owner}` : ''}
                  </span>
                </div>
                {h.auto_cleanup ? (
                  <p className="delete-household__note">
                    Hearth will stop its agents, delete their folders on this computer, and sign it out.
                  </p>
                ) : (
                  <>
                    <p className="delete-household__note">
                      {h.online
                        ? 'This computer runs an older version of Hearth that can’t clean up after itself.'
                        : 'This computer can’t be reached right now.'}{' '}
                      Hearth on it will stop working, but its files stay until you remove them. On that
                      computer, run <code>hearth stop</code>, then delete <code>~/.hearth</code> and the
                      household’s folder in <code>~/hearth_agents</code>.
                    </p>
                    <label className="delete-household__check">
                      <input
                        type="checkbox"
                        checked={!!acknowledged[h.host_id]}
                        onChange={(e) =>
                          setAcknowledged((a) => ({ ...a, [h.host_id]: e.target.checked }))
                        }
                      />
                      <span>I understand files stay on {h.hostname}</span>
                    </label>
                  </>
                )}
              </div>
            ))}
          </div>
        </Group>
        <details className="delete-household__details">
          <summary>Keep using a computer with another household?</summary>
          <p>
            After deleting, on that computer run <code>hearth host forget</code>, then{' '}
            <code>hearth login --org &lt;household&gt;</code>. Hearth itself stays installed.
          </p>
        </details>
        <StepButtons onBack={back} onNext={next} nextDisabled={!allAcknowledged} />
      </div>
    )
  }

  // ---- 3. Screens, speakers and accounts ----
  if (step === 'elsewhere') {
    const voice = devices.filter((d) => d.form_factor === 'voice')
    const screens = devices.filter((d) => d.form_factor !== 'voice')
    return (
      <div className="about">
        {progress}
        {screens.length > 0 && (
          <>
            <SectionHeader>Screens</SectionHeader>
            <Group>
              <div className="delete-household__body">
                {screens.map((d) => (
                  <div key={d.id} className="delete-household__row">
                    <span className="delete-household__row-name">{d.name}</span>
                  </div>
                ))}
                <p className="delete-household__note">They’ll go back to their pairing screen.</p>
              </div>
            </Group>
          </>
        )}
        {voice.length > 0 && (
          <>
            <SectionHeader>Voice</SectionHeader>
            <Group>
              <div className="delete-household__body">
                {voice.map((d) => (
                  <div key={d.id} className="delete-household__row">
                    <span className="delete-household__row-name">{d.name}</span>
                  </div>
                ))}
                <p className="delete-household__note">
                  Voice devices stop reaching Hearth. Afterwards, remove the Hearth Cmd integration in Home
                  Assistant (Settings → Devices &amp; services).
                </p>
              </div>
            </Group>
          </>
        )}
        {connections.length > 0 && (
          <>
            <SectionHeader>Connected accounts</SectionHeader>
            <Group>
              <div className="delete-household__body">
                {connections.map((c) => (
                  <div key={c.id} className="delete-household__row">
                    <span className="delete-household__row-name">{c.display_name}</span>
                    <span className="delete-household__row-meta">{c.plugin}</span>
                  </div>
                ))}
                <p className="delete-household__note">
                  Hearth deletes its copy of these connections. To be sure an account no longer lists Hearth,
                  check its connected-apps settings afterwards (for Google,{' '}
                  <a href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer">
                    myaccount.google.com/permissions
                  </a>
                  ).
                </p>
              </div>
            </Group>
          </>
        )}
        <StepButtons onBack={back} onNext={next} />
      </div>
    )
  }

  // ---- 5. Done ----
  if (step === 'done' && result) {
    const manual = result.manual_steps ?? []
    const cleaned = (result.hosts ?? []).filter((h) => h.cleaned_up)
    return (
      <div className="about">
        <SectionHeader>{p.name} was deleted</SectionHeader>
        {cleaned.length > 0 && (
          <Group>
            <div className="delete-household__body">
              {cleaned.map((h) => (
                <p key={h.host_id} className="delete-household__note">
                  {h.hostname}: agents stopped, folders deleted, signed out.
                </p>
              ))}
            </div>
          </Group>
        )}
        {manual.length > 0 ? (
          <>
            <SectionHeader>Still to do by hand</SectionHeader>
            <Group>
              <div className="delete-household__body">
                <ol className="delete-household__steps">
                  {manual.map((m, i) => (
                    <li key={i}>
                      <div className="delete-household__row-name">{m.title}</div>
                      <div className="delete-household__note">{m.detail}</div>
                    </li>
                  ))}
                </ol>
                <p className="delete-household__note">We’ve emailed you this list too.</p>
              </div>
            </Group>
          </>
        ) : (
          <p className="about__hint delete-household__hint">There’s nothing left for you to do.</p>
        )}
        <div className="delete-household__buttons">
          <button className="delete-household__button delete-household__button--primary" onClick={popSheet}>
            Done
          </button>
        </div>
      </div>
    )
  }

  // ---- 4. Confirm (and deleting) ----
  const deleting = step === 'deleting'
  return (
    <div className="about">
      {progress}
      <SectionHeader>Confirm</SectionHeader>
      <Group>
        <div className="delete-household__body">
          <label className="delete-household__label" htmlFor="delete-household-name">
            Type <b>{p.name}</b> to confirm
          </label>
          <input
            id="delete-household-name"
            className="delete-household__input"
            value={typedName}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            disabled={codeSent || deleting}
            onChange={(e) => {
              setTypedName(e.target.value)
              setError(null)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void sendCode()
            }}
          />
          {!codeSent ? (
            <button
              className="delete-household__button"
              onClick={() => void sendCode()}
              disabled={!nameMatches || busy}
            >
              {busy ? 'Sending code…' : `Email a code to ${email}`}
            </button>
          ) : (
            <>
              <label className="delete-household__label" htmlFor="delete-household-code">
                Enter the 6-digit code we sent to {email}
              </label>
              <input
                id="delete-household-code"
                ref={codeRef}
                className="delete-household__input delete-household__input--code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                value={code}
                maxLength={6}
                disabled={deleting}
                onChange={(e) => onCodeChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void confirmDelete()
                }}
              />
              <button
                className="delete-household__button delete-household__button--danger"
                onClick={() => void confirmDelete()}
                disabled={code.length !== 6 || busy || deleting}
              >
                {deleting ? 'Deleting…' : busy ? 'Checking…' : `Delete ${p.name} permanently`}
              </button>
              {!deleting && (
                <button
                  className="delete-household__link"
                  onClick={() => {
                    setCodeSent(false)
                    setCode('')
                    setError(null)
                  }}
                  disabled={busy}
                >
                  Send a new code
                </button>
              )}
            </>
          )}
          {deleting && (
            <p className="delete-household__note">
              {hosts.some((h) => h.auto_cleanup)
                ? 'Stopping agents and cleaning up your computers. This can take up to half a minute.'
                : 'This takes a few seconds.'}
            </p>
          )}
        </div>
      </Group>
      {error && <p className="about__error">{error}</p>}
      {!deleting && <StepButtons onBack={back} />}
    </div>
  )
}

function StepButtons({
  onBack,
  onNext,
  nextDisabled,
}: {
  onBack?: () => void
  onNext?: () => void
  nextDisabled?: boolean
}) {
  return (
    <div className="delete-household__buttons">
      {onBack && (
        <button className="delete-household__button" onClick={onBack}>
          Back
        </button>
      )}
      {onNext && (
        <button
          className="delete-household__button delete-household__button--primary"
          onClick={onNext}
          disabled={nextDisabled}
        >
          Next
        </button>
      )}
    </div>
  )
}

function plural(n: number, noun: string): string {
  if (n <= 0) return ''
  return `${n.toLocaleString()} ${noun}${n === 1 ? '' : 's'}`
}
